<template>
  <q-dialog v-model="isOpenSync" persistent>
    <q-card style="width: 40rem; max-width: 80vw">
      <!-- Dialog header -->
      <q-card-section class="row items-center">
        <h6>Create Advertisement</h6>
        <q-space />
        <q-btn flat round dense icon="close" @click="closeDialog" />
      </q-card-section>

      <!-- Dialog Content -->
      <q-card-section class="overflow-auto">
        <div class="row q-col-gutter-md">
          <div v-if="transporter && transporter.companyLogo" class="col-6 offset-3">
            <q-img :src="transporter.companyLogo" alt="transporter's profile picture" contain style="height: 64px" />
          </div>

          <div class="col-12">
            <q-select
              v-model="transporter"
              :options="transporterOptions"
              option-label="tradingName"
              option-value="id"
              label="Search transporter"
              autofocus
              input-debounce="500"
              use-input
              hide-selected
              fill-input
              clearable
              hint="Transporter to advertise"
              error-message="Transporter is required"
              :disable="!!advert"
              :option-disable="() => !!advert"
              :error="$v.$dirty && $v.transporter.$invalid"
              @filter="filter"
            >
              <template #no-option>
                <q-item>
                  <q-item-section class="text-grey"> No transporter found. </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>

          <div class="col-12 col-sm-6">
            <q-input
              v-model="title"
              label="Title"
              hint="Title of advertisement"
              maxlength="64"
              counter
              error-message="Title is required"
              :error="$v.$dirty && $v.title.$invalid"
            />
          </div>

          <div class="col-12 col-sm-6">
            <q-input
              v-model="second"
              label="Second line"
              hint="Caption of advertisement"
              maxlength="64"
              counter
              error-message="Second line is required"
              :error="$v.$dirty && $v.second.$invalid"
            />
          </div>

          <div class="col-12 col-sm-6">
            <q-select
              v-model="status"
              :options="statusOptions"
              label="Status"
              hint="Status of advertisement"
              emit-value
              map-options
              error-message="Status is required"
              :error="$v.$dirty && $v.status.$invalid"
            />
          </div>

          <div class="col-12 col-sm-6">
            <q-select
              v-model="country"
              :options="countryOptions"
              label="Country"
              hint="Adverts depends on different country"
            />
          </div>

          <div class="col-12">
            <q-input v-model="note" outlined type="textarea" label="Admin note (optional)" />
          </div>

          <div v-if="error" class="col-12">
            <p class="text-negative">{{ error }}</p>
          </div>
        </div>
      </q-card-section>

      <!-- Dialog actions -->
      <q-card-actions align="right">
        <q-btn flat color="grey" label="Cancel" :loading="isLoading" @click="closeDialog" />
        <q-btn
          v-if="advert"
          color="primary"
          label="Confirm Edit"
          class="q-mr-sm"
          :loading="isLoading"
          @click="editAd"
        />
        <q-btn v-else color="primary" label="Create" class="q-mr-sm" :loading="isLoading" @click="createAd" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import required from 'vuelidate/lib/validators/required';
import { getErrorMessage } from '@/utils';

export default {
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    advert: {
      type: Object,
      required: false,
      default: () => {},
    },
    onCreateSuccess: {
      type: Function,
      required: true,
    },
    onEditSuccess: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      transporter: undefined,
      transporterOptions: [],
      status: undefined,
      statusOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
      ],
      title: '',
      second: '',
      country: 'Australia',
      countryOptions: ['Australia'],
      note: '',
      isLoading: false,
      error: undefined,
    };
  },

  computed: {
    isOpenSync: {
      get() {
        return this.isOpen;
      },
      set(value) {
        this.$emit('update:isOpen', value);
      },
    },
  },

  watch: {
    advert(val) {
      if (val) {
        this.setValue(val);
      } else {
        this.setValue();
      }
    },
  },

  methods: {
    async filter(val, update) {
      try {
        const params = val ? { q: val } : {};
        const { data } = await this.$axios.get('/admin/ad/company', {
          params,
        });

        update(() => {
          this.transporterOptions = data;
        });
      } catch (error) {
        this.error = getErrorMessage(error);
      }
    },
    setValue(ad = {}) {
      this.$v.$reset();
      this.transporter = ad.company;
      this.status = ad.isActive || false;
      this.title = ad.title;
      this.second = ad.second;
      this.country = ad.country || 'Australia';
      this.note = ad.noteByUser && ad.noteByUser.length ? ad.noteByUser[0].note : undefined;
    },
    closeDialog() {
      if (this.advert) {
        this.$emit('update:advert', undefined);
      }

      this.isOpenSync = false;
    },
    async createAd() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      const payload = {
        title: this.title,
        second: this.second,
        isActive: this.status,
        country: this.country,
        note: this.note,
        transporterId: this.transporter.id,
      };

      this.isLoading = true;

      try {
        const { data } = await this.$axios.post('/admin/ad', payload);

        this.closeDialog();
        this.onCreateSuccess(data);
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isLoading = false;
        this.$v.$reset();
      }
    },
    async editAd() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      const payload = {
        title: this.title,
        second: this.second,
        isActive: this.status,
        country: this.country,
        note: this.note,
        transporterId: this.transporter.id,
      };

      this.isLoading = true;

      try {
        const { data } = await this.$axios.put(`/admin/ad/${this.advert.id}`, payload);

        this.closeDialog();
        this.onEditSuccess(data);
      } catch (error) {
        this.error = getErrorMessage(error);
      } finally {
        this.isLoading = false;
        this.$v.$reset();
      }
    },
  },

  /* eslint-disable func-names */
  validations: {
    transporter: {
      required,
    },
    status: {
      required,
    },
    title: {
      required,
    },
    second: {
      required,
    },
  },
};
</script>
